import { eff } from "@/core/utils/effect-ts-abbr";
import { AppRoute } from "@/router/index";
import { AppLanguageCode } from "@/shared/api-contract/api-contract";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import { ByAppLangCode } from "@/shared/i18n/translations";
import { getEnvValue } from "@/shared/models/environment";
import Response from "@/shared/models/response";
import { ApiRouter, PageTemplate } from "@/shared/models/router";
import axios from "axios";
import { computed, shallowRef } from "vue";
import { RouteLocationRaw, Router } from "vue-router";

const brandLabel = getEnvValue("BRAND_LABEL");

const templates = {
    // homepage: Home,
    homepage: () => import("../core/home/views/home.vue"),
    contacts: () =>
        import(/* webpackChunkName: "contacts" */ "../core/contacts/views/Contacts.vue"),
    "our-roots": () => import("../core/our-roots/our-roots.vue"),
    "our-workings": () => import("../core/our-workings/OurWorkings.vue"),
    privacy: () => import("../core/privacy/Privacy.vue"),
    "work-in-progress": () => import("../core/work-in-progress/WorkInProgress.vue"),
    "terms-and-conditions": () =>
        import("../core/terms-and-conditions/terms-and-conditions.vue"),
} as const;
const titleByTemplates: { readonly [K in keyof typeof templates]: string } = {
    homepage: `pagesTitles.home`,
    contacts: `pagesTitles.contactUs`,
    "our-roots": `pagesTitles.ourRoots`,
    "our-workings": `pagesTitles.ourWorkings`,
    privacy: `pagesTitles.privacy`,
    "work-in-progress": `pagesTitles.workInProgress`,
    "terms-and-conditions": `pagesTitles.termsAndConditions`,
};

const exceptTemplate: Array<keyof typeof templates> = [];
if (brandLabel === "astara") {
    exceptTemplate.push("contacts", "our-roots", "our-workings");
}

const homepageID = 0;

type RoutesByLang = ByAppLangCode<eff.dict.Dictionary<AppRoute>>;

class RouterModel {
    rawRoutes = shallowRef<readonly PageTemplate[]>([]);
    // rawRegions = shallowRef<readonly PageTemplate[]>([]);
    routes = shallowRef<RoutesByLang>({
        [AppLanguageCode.en]: {},
        [AppLanguageCode.fr]: {},
        [AppLanguageCode.de]: {},
        [AppLanguageCode.es]: {},
        [AppLanguageCode.it]: {},
        [AppLanguageCode.nl]: {},
    } as RoutesByLang);
    routesByName = shallowRef<eff.dict.Dictionary<AppRoute>>({});
    regionsByTemplate = shallowRef<Record<string /* name */, PageTemplate>>({});
    router!: Router;
    homepageRoute = shallowRef<AppRoute>(undefined as unknown as AppRoute);

    currentSlug = computed(() => {
        const slug = (this.router?.currentRoute.value.meta.slug as string) || "";

        return slug.startsWith("/") ? slug.substring(1) : slug;
    });

    back = (fallback: RouteLocationRaw = "/") => {
        if (history.state.back) {
            this.router.back();
        } else {
            this.router.push(fallback);
        }
    };

    loadRoutes = async () => {
        const apiRouterData = await axios
            .get<Response<ApiRouter>>("/api/pages/routes")
            .then((response) => response.data.data)
            .catch(console.log);
        if (!apiRouterData?.pages) return;
        this.rawRoutes.value = apiRouterData.pages;
        apiRouterData.regions.forEach((template) => {
            this.regionsByTemplate.value[template.template] = template;
        });

        this.routes.value = eff.fn.pipe(
            this.rawRoutes.value || [],
            eff.array.reduce({} as Record<string, AppRoute>, (acc, it) => {
                if (it.template in templates === false) {
                    return acc;
                }

                if (exceptTemplate.includes(it.template as keyof typeof templates)) {
                    return acc;
                }

                const template = templates[it.template as keyof typeof templates];
                const sameRouteByLang = it.id;
                it.locales.forEach((lang) => {
                    const path = "/" + lang + it.path[lang];
                    const name = lang + "." + it.name[lang];
                    const id = it.id[lang];
                    acc[path] = {
                        id,
                        name,
                        path,
                        lang,
                        template,
                        sameRouteByLang,
                        titleTranslateKey:
                            titleByTemplates[it.template as keyof typeof templates],
                        slug: it.slug[lang],
                    };
                });

                if (
                    appLanguageSwitcherModel.lang.value in it.path &&
                    it.path[appLanguageSwitcherModel.lang.value] === "/"
                ) {
                    this.homepageRoute.value = {
                        id: homepageID,
                        name: "homepage",
                        path: "/",
                        template,
                        lang: appLanguageSwitcherModel.lang.value,
                        sameRouteByLang,
                        titleTranslateKey:
                            titleByTemplates[it.template as keyof typeof templates],
                        slug: it.slug[appLanguageSwitcherModel.lang.value],
                    };
                    acc["/"] = this.homepageRoute.value;
                }

                return acc;
            }),
            eff.dict.toArray,
            eff.array.reduce(
                {
                    [AppLanguageCode.en]: {},
                    [AppLanguageCode.fr]: {},
                    [AppLanguageCode.de]: {},
                    [AppLanguageCode.es]: {},
                    [AppLanguageCode.it]: {},
                    [AppLanguageCode.nl]: {},
                } as Record<AppLanguageCode, Record<string /* id */, AppRoute>>,
                (acc, { tuple: [, route] }) => {
                    if (!acc[route.lang]) {
                        acc[route.lang] = {};
                    }

                    acc[route.lang][route.id] = route;

                    return acc;
                },
            ),
        );

        this.routesByName.value = eff.fn.pipe(
            this.routes.value,
            eff.dict.map(
                eff.fn.flow(
                    eff.dict.toArray,
                    eff.array.map(({ tuple: [, route] }) => route),
                ),
            ),
            eff.dict.toArray,
            eff.array.map(({ tuple: [, routes] }) => routes),
            eff.array.flatten,
            eff.array.reduce({} as Record<string /* name */, AppRoute>, (acc, route) => {
                acc[route.name] = route;

                return acc;
            }),
        );

        return this.routes;
    };
}

export const routerModel = new RouterModel();
